import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminApi } from '@vmgate/common';
import type { LicenseListResponse, LicenseRecord } from '@vmgate/common';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { ErrorState } from '../components/common/ErrorState';
import { PaginationControls } from '../components/common/PaginationControls';
import { StatusBadge } from '../components/common/StatusBadge';
import { formatDateTime } from '../utils/format';

const STATUS_OPTIONS = ['ACTIVE', 'SUSPENDED', 'REVOKED'];

export function LicensesPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [customerFilter, setCustomerFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const licensesQuery = useQuery<LicenseListResponse>({
    queryKey: ['licenses', page, statusFilter, customerFilter],
    queryFn: () =>
      adminApi.getLicenses({
        page,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        customer_id: customerFilter || undefined
      }),
    placeholderData: (previousData) => previousData
  });
  const { data, isLoading, isError, refetch, isFetching } = licensesQuery;

  const mutation = useMutation({
    mutationFn: (payload: { action: 'activate' | 'deactivate' | 'revoke'; license_ids: string[] }) =>
      adminApi.bulkUpdateLicenses(payload),
    onSuccess: () => {
      setSelectedIds([]);
      refetch();
    }
  });

  const toggleSelection = (license: LicenseRecord) => {
    setSelectedIds((prev) =>
      prev.includes(license.license_id)
        ? prev.filter((id) => id !== license.license_id)
        : [...prev, license.license_id]
    );
  };

  const toggleSelectAll = (licenses: LicenseRecord[]) => {
    if (selectedIds.length === licenses.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(licenses.map((license) => license.license_id));
    }
  };

  const executeAction = (action: 'activate' | 'deactivate' | 'revoke') => {
    if (!selectedIds.length) return;
    mutation.mutate({ action, license_ids: selectedIds });
  };

  if (isLoading) {
    return <LoadingScreen message="Loading licenses..." />;
  }

  if (isError || !data) {
    return <ErrorState message="Unable to load licenses." onRetry={() => refetch()} />;
  }

  const licenses = data.licenses;
  const totalPages = data.pages ?? 1;

  return (
    <div className="surface surface-padding" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Licenses</h2>
          <div className="text-muted">Monitor issued licenses and perform bulk operations.</div>
        </div>
        <div className="action-buttons">
          <button
            type="button"
            className="btn"
            disabled={!selectedIds.length || mutation.isPending}
            onClick={() => executeAction('activate')}
          >
            Activate
          </button>
          <button
            type="button"
            className="btn"
            disabled={!selectedIds.length || mutation.isPending}
            onClick={() => executeAction('deactivate')}
          >
            Suspend
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={!selectedIds.length || mutation.isPending}
            onClick={() => executeAction('revoke')}
          >
            Revoke
          </button>
        </div>
      </header>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <label className="text-muted" style={{ display: 'block', marginBottom: '0.4rem' }}>
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            style={selectStyle}
          >
            <option value="ALL">All</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-muted" style={{ display: 'block', marginBottom: '0.4rem' }}>
            Filter by customer ID
          </label>
          <input
            type="text"
            placeholder="CUST001"
            value={customerFilter}
            onChange={(event) => {
              setCustomerFilter(event.target.value.toUpperCase());
              setPage(1);
            }}
            style={selectStyle}
          />
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === licenses.length && licenses.length > 0}
                  onChange={() => toggleSelectAll(licenses)}
                  aria-label="Toggle select all"
                />
              </th>
              <th>License</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Issued</th>
              <th>Device</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {licenses.map((license) => {
              const device = license.device_info;
              return (
                <tr key={license.license_id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(license.license_id)}
                      onChange={() => toggleSelection(license)}
                      aria-label={`Select ${license.license_id}`}
                    />
                  </td>
                  <td>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{license.license_id}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      S/N: {license.device_info?.build_serial ?? '—'}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Type: {license.license_type}
                    </div>
                  </td>
                  <td>
                    <div>{license.customer_name ?? license.customer_id}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Token: {license.deployment_token ? license.deployment_token.slice(0, 10) + '…' : '—'}
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={license.status} />
                  </td>
                  <td>{formatDateTime(license.created_at)}</td>
                  <td>
                    {device ? (
                      <div>
                        <div>{device.build_brand ?? 'Unknown'} </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {device.build_model ?? ''}
                        </div>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{license.certificate_email || '—'}</td>
                </tr>
              );
            })}
            {licenses.length === 0 && (
              <tr>
                <td colSpan={7} className="text-muted" style={{ textAlign: 'center', padding: '1rem' }}>
                  No licenses to display.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls
        page={data.current_page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={isFetching || mutation.isPending}
      />
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: '10px',
  padding: '0.6rem 0.8rem',
  fontSize: '0.95rem',
  background: '#fff'
};

